// MCHD Room Scheduler - Admin Session Times Management
// Main page component for managing room session times

import { useState } from 'react';
import {
  Container,
  Title,
  Tabs,
  Group,
  Button,
  Stack,
  Alert,
  Loader,
  Center,
  Text
} from '@mantine/core';
import { IconPlus, IconClock, IconAlertCircle } from '@tabler/icons-react';

// Import components
import { SessionTimeStats } from '../../../components/organisms/SessionTimeStats';
import { SessionTimeList } from '../../../components/organisms/SessionTimeList';
import { SessionTimeForm } from '../../coordinator/session-times/SessionTimeForm';

// Import hooks
import {
  useSessionTimes,
  useRoomSessionTimeStats,
  useCreateSessionTime,
  useDeleteSessionTime
} from '../../../hooks/admin/useSessionTimes';
import { useBuildingsForSelection } from '../../../hooks/admin/useBuildings';
import { useRoomsForSelection } from '../../../hooks/admin/useRooms';

// Import types
import type { RoomSessionTimeWithDetails, CreateRoomSessionTimeData } from '../../../api/endpoints/session-times';

// =============================================================================
// COMPONENT
// =============================================================================

/**
 * Admin Session Times Management Page
 * Provides full CRUD interface for managing room session times
 */
export function SessionTimeManagement() {
  // =============================================================================
  // STATE
  // =============================================================================

  const [activeTab, setActiveTab] = useState<string>('overview');
  const [selectedSessionTime, setSelectedSessionTime] = useState<RoomSessionTimeWithDetails | null>(null);
  const [showForm, setShowForm] = useState<boolean>(false);
  const [filters, setFilters] = useState<{
    search: string;
    roomId?: string | undefined;
    buildingId?: string | undefined;
    isActive?: boolean | undefined;
    sortBy: 'name' | 'startTime' | 'endTime' | 'createdAt' | 'updatedAt';
    sortOrder: 'asc' | 'desc';
  }>({
    search: '',
    sortBy: 'name',
    sortOrder: 'asc'
  });

  // =============================================================================
  // HOOKS
  // =============================================================================

  // Data hooks - Use correct hook for listing all session times (not room-specific)
  const { data: sessionTimesData, isLoading: sessionTimesLoading, error: sessionTimesError } = useSessionTimes(filters.roomId);

  const { data: statsData, isLoading: statsLoading, error: statsError } = useRoomSessionTimeStats({
    ...(filters.roomId && { roomId: filters.roomId }),
    ...(filters.buildingId && { buildingId: filters.buildingId })
  });

  // Selection hooks
  const { data: buildingsData } = useBuildingsForSelection();
  const { data: roomsData } = useRoomsForSelection();
  
  const buildings = buildingsData?.data?.buildings || [];
  const rooms = roomsData || [];

  // Mutation hooks
  const createSessionTimeMutation = useCreateSessionTime();
  const deleteSessionTimeMutation = useDeleteSessionTime();

  // =============================================================================
  // HANDLERS
  // =============================================================================

  /**
   * Handle filter changes
   */
  const handleFilterChange = (newFilters: Partial<typeof filters>) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
  };

  /**
   * Handle create session time
   */
  const handleCreateSessionTime = (data: CreateRoomSessionTimeData) => {
    createSessionTimeMutation.mutate(data, {
      onSuccess: () => {
        setShowForm(false);
        setSelectedSessionTime(null);
      }
    });
  };

  /**
   * Handle update session time
   */
  const handleUpdateSessionTime = (id: string, data: Partial<CreateRoomSessionTimeData>) => {
    // This will be implemented when we add the update mutation
    console.log('Update session time:', id, data);
  };

  /**
   * Handle delete session time
   */
  const handleDeleteSessionTime = (id: string) => {
    if (confirm('Are you sure you want to delete this session time?')) {
      deleteSessionTimeMutation.mutate(id);
    }
  };

  /**
   * Handle select session time for editing
   */
  const handleSelectSessionTime = (sessionTime: RoomSessionTimeWithDetails) => {
    setSelectedSessionTime(sessionTime);
    setShowForm(true);
    setActiveTab('form');
  };

  /**
   * Handle close form
   */
  const handleCloseForm = () => {
    setShowForm(false);
    setSelectedSessionTime(null);
    setActiveTab('overview');
  };

  // =============================================================================
  // RENDER
  // =============================================================================

  return (
    <Container size="xl" py="md">
      <Stack gap="lg">
        {/* Header */}
        <Group justify="space-between" align="flex-start">
          <div>
            <Title order={1} size="h2" fw={600}>
              Session Times Management
            </Title>
            <Text c="dimmed" size="sm" mt={4}>
              Manage room session times and schedules
            </Text>
          </div>
          
          <Button
            leftSection={<IconPlus size={16} />}
            onClick={() => {
              setSelectedSessionTime(null);
              setShowForm(true);
              setActiveTab('form');
            }}
            disabled={showForm}
          >
            Create Session Time
          </Button>
        </Group>

        {/* Error Alerts */}
        {(sessionTimesError || statsError) && (
          <Alert
            icon={<IconAlertCircle size={16} />}
            title="Error Loading Data"
            color="red"
            variant="light"
          >
            {sessionTimesError?.message || statsError?.message || 'An error occurred while loading data'}
          </Alert>
        )}

        {/* Loading State */}
        {(sessionTimesLoading || statsLoading) && (
          <Center py="xl">
            <Stack align="center" gap="md">
              <Loader size="md" />
              <Text c="dimmed">Loading session times data...</Text>
            </Stack>
          </Center>
        )}

        {/* Main Content */}
        {!sessionTimesLoading && !statsLoading && (
          <Tabs value={activeTab} onChange={(value) => setActiveTab(value || 'overview')}>
            <Tabs.List>
              <Tabs.Tab value="overview" leftSection={<IconClock size={16} />}>
                Overview
              </Tabs.Tab>
              <Tabs.Tab value="list" leftSection={<IconClock size={16} />}>
                Session Times List
              </Tabs.Tab>
              {showForm && (
                <Tabs.Tab value="form" leftSection={<IconPlus size={16} />}>
                  {selectedSessionTime ? 'Edit Session Time' : 'Create Session Time'}
                </Tabs.Tab>
              )}
            </Tabs.List>

            {/* Overview Tab */}
            <Tabs.Panel value="overview" pt="lg">
              <SessionTimeStats
                stats={statsData || undefined}
                isLoading={statsLoading}
                error={statsError}
              />
            </Tabs.Panel>

            {/* List Tab */}
            <Tabs.Panel value="list" pt="lg">
              <SessionTimeList
                sessionTimes={sessionTimesData?.data || []}
                pagination={sessionTimesData?.pagination || undefined}
                isLoading={sessionTimesLoading}
                error={sessionTimesError}
                buildings={buildings}
                rooms={rooms}
                filters={filters}
                onFilterChange={handleFilterChange}
                onSelectSessionTime={handleSelectSessionTime}
                onDeleteSessionTime={handleDeleteSessionTime}
                onUpdateSessionTime={handleUpdateSessionTime}
              />
            </Tabs.Panel>

            {/* Form Tab */}
            {showForm && (
              <Tabs.Panel value="form" pt="lg">
                <SessionTimeForm
                  sessionTime={selectedSessionTime}
                  buildings={buildings}
                  rooms={rooms}
                  onSubmit={handleCreateSessionTime}
                  onCancel={handleCloseForm}
                  isLoading={createSessionTimeMutation.isPending}
                />
              </Tabs.Panel>
            )}
          </Tabs>
        )}
      </Stack>
    </Container>
  );
}
